import { CommonModule } from '@angular/common'
import { AfterViewChecked, Component, DestroyRef, ElementRef, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms'
import { ComponentVariableDefinition, EmailComponentType } from '../../interfaces/email-component.interface'
import { EmailTheme } from '../../interfaces/email-theme.interface'
import { EmailComposerService } from '../../services/email-composer.service'
import { MjmlService } from '../../services/mjml.service'
import { MjmlEditorComponent } from '../mjml-editor/mjml-editor.component'

@Component({
  selector: 'ngx-component-editor',
  templateUrl: './component-editor.component.html',
  styleUrls: ['./component-editor.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MjmlEditorComponent]
})
export class ComponentEditorComponent implements OnInit, OnChanges, AfterViewChecked {
  @Input() componentId: number | null = null
  @Output() close = new EventEmitter<void>()
  @Output() saved = new EventEmitter<EmailComponentType>()

  @ViewChild('nameInput') nameInput!: ElementRef<HTMLInputElement>
  @ViewChild('descriptionInput') descriptionInput!: ElementRef<HTMLInputElement>

  componentForm: FormGroup
  loading = false
  saving = false
  isEditMode = false
  showSavedMessage = false

  availableTypes = ['text', 'url', 'color', 'number', 'boolean']
  availableCategories = ['layout', 'content', 'media', 'navigation']

  // Inline editing state
  isEditingName = false
  isEditingDescription = false
  tempName = ''
  tempDescription = ''
  private focusNameInput = false
  private focusDescriptionInput = false

  mjmlPlaceholder = `<mj-section background-color="{{theme.background_color}}">
  <mj-column>
    <mj-text font-size="{{theme.font_size}}" color="{{theme.text_color}}">
      <h2>{{component.title}}</h2>
      <p>{{component.description}}</p>
    </mj-text>
  </mj-column>
</mj-section>`

  selectedTheme: EmailTheme | null = null // Will be populated from email composer service
  availableThemes: EmailTheme[] = []
  componentVariables: Record<string, any> = {}

  private readonly _destroyRef = inject(DestroyRef)

  constructor(private fb: FormBuilder, private emailComposerService: EmailComposerService, private mjmlService: MjmlService) {
    this.componentForm = this.createForm()
  }

  ngOnInit(): void {
    this.checkEditMode()
    this.loadSelectedTheme()
    this.loadAvailableThemes()
    this.updateComponentVariables()
    this.subscribeToVariableChanges()
  }

  private subscribeToVariableChanges(): void {
    // Listen to changes in the variables form array
    this.componentForm
      .get('variables')
      ?.valueChanges.pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this.updateComponentVariables()
      })
  }

  private loadSelectedTheme(): void {
    this.emailComposerService
      .getSelectedTheme()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((theme) => {
        this.selectedTheme = theme
      })
  }

  private loadAvailableThemes(): void {
    this.emailComposerService
      .getThemes(undefined, true) // Get only active themes
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (response) => {
          this.availableThemes = response.data
          // Select the first default theme, or fallback to first theme
          const defaultTheme = this.availableThemes.find((t) => t.is_default) || this.availableThemes[0] || null
          if (defaultTheme) {
            this.selectedTheme = defaultTheme
            this.emailComposerService.setSelectedTheme(defaultTheme)
          }
        },
        error: (error) => {
          console.error('Failed to load themes:', error)
        }
      })
  }

  private updateComponentVariables(): void {
    // Build component variables from form variables
    const variablesArray = this.componentForm.get('variables') as FormArray
    const variables: Record<string, any> = {}

    variablesArray.controls.forEach((control, index) => {
      const variableControl = control as FormGroup
      const name = variableControl.get('name')?.value
      const defaultValue = variableControl.get('default_value')?.value
      const type = variableControl.get('type')?.value

      if (name) {
        variables[name] = this.convertValueByType(defaultValue, type)
      }
    })

    this.componentVariables = variables
  }

  private convertValueByType(value: any, type: string): any {
    if (!value) return value

    switch (type) {
      case 'number':
        return Number(value)
      case 'boolean':
        return value === 'true' || value === true
      case 'color':
      case 'text':
      case 'url':
      default:
        return String(value)
    }
  }

  onThemeChange(theme: EmailTheme): void {
    this.selectedTheme = theme
    this.emailComposerService.setSelectedTheme(theme)
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['componentId']) {
      this.checkEditMode()
    }
  }

  private checkEditMode(): void {
    if (this.componentId) {
      this.isEditMode = true
      this.loadComponent()
    } else {
      this.isEditMode = false
      this.componentForm.reset()
      this.componentForm = this.createForm()
    }
  }

  ngAfterViewChecked(): void {
    if (this.focusNameInput && this.nameInput) {
      this.nameInput.nativeElement.focus()
      this.nameInput.nativeElement.select()
      this.focusNameInput = false
    }
    if (this.focusDescriptionInput && this.descriptionInput) {
      this.descriptionInput.nativeElement.focus()
      this.descriptionInput.nativeElement.select()
      this.focusDescriptionInput = false
    }
  }

  private createForm(): FormGroup {
    return this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(255)]],
      slug: ['', [Validators.pattern(/^[a-z0-9\-_]+$/)]],
      description: ['', [Validators.maxLength(1000)]],
      category: ['content', [Validators.required, Validators.maxLength(100)]],
      mjml_template: ['<mj-section><mj-column><mj-text>{{component.content}}</mj-text></mj-column></mj-section>', [Validators.required]],
      css_styles: ['{}'],
      preview_data: ['{}'],
      is_active: [true],
      variables: this.fb.array([])
    })
  }

  get variablesArray(): FormArray {
    return this.componentForm.get('variables') as FormArray
  }

  private loadComponent(): void {
    if (!this.componentId) return

    this.loading = true
    this.emailComposerService
      .getComponentType(this.componentId)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (response) => {
          this.populateForm(response.data)
          this.loading = false
        },
        error: (error) => {
          console.error('Error loading component:', error)
          this.loading = false
        }
      })
  }

  private populateForm(component: EmailComponentType): void {
    // Support both mjml_template and html_template for backward compatibility
    const template = component.mjml_template || component.html_template || ''

    this.componentForm.patchValue({
      name: component.name,
      slug: component.slug,
      description: component.description,
      category: component.category,
      mjml_template: template,
      css_styles: JSON.stringify(component.css_styles || {}, null, 2),
      preview_data: JSON.stringify(component.preview_data || {}, null, 2),
      is_active: component.is_active
    })

    // Clear existing variables
    while (this.variablesArray.length !== 0) {
      this.variablesArray.removeAt(0)
    }

    // Add component variables
    if (component.variables) {
      component.variables.forEach((variable) => {
        this.addVariable(variable)
      })
    }

    // Fix any existing variable slug validation issues
    this._fixVariableSlugs()
  }

  addVariable(variable?: ComponentVariableDefinition): void {
    // Generate default values for new variables
    const defaultName = variable?.name || `variable_${this.variablesArray.length + 1}`
    const defaultSlug = variable?.slug || defaultName.toLowerCase().replace(/[^a-z0-9]/g, '_')

    const variableForm = this.fb.group({
      id: [variable?.id || null],
      name: [defaultName, [Validators.required, Validators.pattern(/^[a-zA-Z_][a-zA-Z0-9_]*$/)]],
      slug: [defaultSlug, [Validators.required, Validators.pattern(/^[a-z0-9_]+$/)]],
      label: [variable?.label || 'Variable Label', [Validators.required, Validators.maxLength(255)]],
      type: [variable?.type || 'text', [Validators.required]],
      default_value: [variable?.default_value || ''],
      validation_rules: [variable?.validation_rules || []],
      is_required: [variable?.is_required || false],
      sort_order: [variable?.sort_order || this.variablesArray.length + 1]
    })

    // Auto-generate slug from name
    variableForm
      .get('name')
      ?.valueChanges.pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((name) => {
        if (name && !variableForm.get('slug')?.dirty) {
          const slug = name.toLowerCase().replace(/[^a-z0-9]/g, '_')
          variableForm.get('slug')?.setValue(slug)
        }
      })

    this.variablesArray.push(variableForm)
  }

  removeVariable(index: number): void {
    this.variablesArray.removeAt(index)
    this.updateVariableOrders()
  }

  moveVariableUp(index: number): void {
    if (index === 0) return
    const variable = this.variablesArray.at(index)
    this.variablesArray.removeAt(index)
    this.variablesArray.insert(index - 1, variable)
    this.updateVariableOrders()
  }

  moveVariableDown(index: number): void {
    if (index === this.variablesArray.length - 1) return
    const variable = this.variablesArray.at(index)
    this.variablesArray.removeAt(index)
    this.variablesArray.insert(index + 1, variable)
    this.updateVariableOrders()
  }

  private updateVariableOrders(): void {
    this.variablesArray.controls.forEach((control, index) => {
      control.get('sort_order')?.setValue(index + 1)
    })
  }

  onNameChange(): void {
    const name = this.componentForm.get('name')?.value
    if (name && !this.componentForm.get('slug')?.dirty) {
      const slug = name
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')
      this.componentForm.get('slug')?.setValue(slug)
    }
  }

  generatePreviewData(): void {
    const variables = this.variablesArray.value
    const previewData: any = {}

    variables.forEach((variable: any) => {
      switch (variable.type) {
        case 'text':
          previewData[variable.name] = variable.default_value || `Sample ${variable.label}`
          break
        case 'url':
          previewData[variable.name] = variable.default_value || 'https://example.com'
          break
        case 'color':
          previewData[variable.name] = variable.default_value || '#3b82f6'
          break
        case 'number':
          previewData[variable.name] = variable.default_value || 42
          break
        case 'boolean':
          previewData[variable.name] = variable.default_value || true
          break
      }
    })

    this.componentForm.get('preview_data')?.setValue(JSON.stringify(previewData, null, 2))
  }

  onSave(): void {
    if (!this.componentForm.valid) {
      this.markFormGroupTouched(this.componentForm)
      return
    }

    this.saving = true
    const formValue = this.componentForm.value

    // Prepare component data
    const componentData: Partial<EmailComponentType> = {
      id: this.componentId,
      name: formValue.name,
      slug: formValue.slug || this.generateSlugFromName(formValue.name),
      description: formValue.description,
      category: formValue.category,
      mjml_template: formValue.mjml_template, // Send raw MJML template without substitutions
      css_styles: this.parseJsonField(formValue.css_styles),
      preview_data: this.parseJsonField(formValue.preview_data),
      is_active: formValue.is_active,
      variables: formValue.variables.map((variable: any, index: number) => ({
        ...variable,
        sort_order: index + 1,
        // Ensure default_value is properly handled for different types
        default_value: this.processDefaultValue(variable.default_value, variable.type)
      }))
    }

    console.log('Saving component data:', componentData) // Debug log

    const request$ = this.mjmlService.compileMjmlForStorage(componentData.mjml_template || '')

    request$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe({
      next: (response) => {
        const html_template = response
        const payload: Partial<EmailComponentType> = { ...componentData, html_template }
        const api$ = this.isEditMode ? this.emailComposerService.updateComponent(this.componentId!, payload) : this.emailComposerService.createComponent(payload)

        api$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe({
          next: (apiResponse) => {
            this.saving = false
            this.showSavedMessage = true

            setTimeout(() => {
              this.showSavedMessage = false
            }, 3000)

            if (!this.isEditMode && apiResponse.data?.id) {
              this.componentId = apiResponse.data.id
              this.isEditMode = true
            }
          },
          error: (error) => {
            this.saving = false
            console.error('Error saving component:', error)
          }
        })
      },
      error: (error) => {
        this.saving = false
        console.error('Error compiling MJML to HTML:', error)
      }
    })
  }

  private generateSlugFromName(name: string): string {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key)
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control)
      } else if (control instanceof FormArray) {
        control.controls.forEach((arrayControl) => {
          if (arrayControl instanceof FormGroup) {
            this.markFormGroupTouched(arrayControl)
          } else {
            arrayControl.markAsTouched()
          }
        })
      } else {
        control?.markAsTouched()
      }
    })
  }

  onClose(): void {
    this.close.emit()
  }

  getValidationRulesString(variable: any): string {
    const rules = variable.get('validation_rules')?.value || []
    return Array.isArray(rules) ? rules.join(', ') : ''
  }

  setValidationRules(variable: any, rulesString: string): void {
    const rules = rulesString
      .split(',')
      .map((rule) => rule.trim())
      .filter((rule) => rule.length > 0)
    variable.get('validation_rules')?.setValue(rules)
  }

  private parseJsonField(value: any): any {
    if (typeof value === 'string') {
      try {
        return JSON.parse(value)
      } catch {
        return {}
      }
    }
    return value || {}
  }

  // Inline editing methods
  startEditingName(): void {
    this.tempName = this.componentForm.get('name')?.value || ''
    this.isEditingName = true
    this.focusNameInput = true
  }

  finishEditingName(): void {
    if (this.tempName.trim()) {
      this.componentForm.get('name')?.setValue(this.tempName)
      this.onNameChange() // Trigger slug generation
    }
    this.isEditingName = false
  }

  cancelEditingName(): void {
    this.tempName = this.componentForm.get('name')?.value || ''
    this.isEditingName = false
  }

  startEditingDescription(): void {
    this.tempDescription = this.componentForm.get('description')?.value || ''
    this.isEditingDescription = true
    this.focusDescriptionInput = true
  }

  finishEditingDescription(): void {
    this.componentForm.get('description')?.setValue(this.tempDescription)
    this.isEditingDescription = false
  }

  cancelEditingDescription(): void {
    this.tempDescription = this.componentForm.get('description')?.value || ''
    this.isEditingDescription = false
  }

  onKeyDown(event: KeyboardEvent, field: 'name' | 'description'): void {
    if (event.key === 'Enter') {
      if (field === 'name') {
        this.finishEditingName()
      } else {
        this.finishEditingDescription()
      }
    } else if (event.key === 'Escape') {
      if (field === 'name') {
        this.cancelEditingName()
      } else {
        this.cancelEditingDescription()
      }
    }
  }

  onCategoryClick(category: string): void {
    this.componentForm.get('category')?.setValue(category)
  }

  toggleActive(): void {
    const currentValue = this.componentForm.get('is_active')?.value
    this.componentForm.get('is_active')?.setValue(!currentValue)
  }

  // Variable management methods
  onVariableTypeClick(variableControl: any, type: string): void {
    variableControl.get('type')?.setValue(type)
  }

  toggleVariableRequired(variableControl: any): void {
    const currentValue = variableControl.get('is_required')?.value
    variableControl.get('is_required')?.setValue(!currentValue)
  }

  // Inline editing for variable labels
  startEditingVariableLabel(index: number): void {
    // We'll use a simple approach - the label field will have inline editing similar to name/description
  }

  updateVariableSlugFromLabel(variableControl: any): void {
    const label = variableControl.get('label')?.value
    if (label && !variableControl.get('name')?.dirty) {
      const slug = this.generateValidVariableName(label)
      variableControl.get('name')?.setValue(slug)
      variableControl.get('slug')?.setValue(slug)
    }
  }

  sanitizeVariableName(variableControl: any): void {
    const currentValue = variableControl.get('name')?.value || ''
    const sanitized = this.generateValidVariableName(currentValue)
    if (currentValue !== sanitized) {
      variableControl.get('name')?.setValue(sanitized)
    }

    // Also update the slug field to match the sanitized name
    const slugValue = variableControl.get('slug')?.value || ''
    const sanitizedSlug = this.generateValidVariableName(currentValue)
    if (slugValue !== sanitizedSlug) {
      variableControl.get('slug')?.setValue(sanitizedSlug)
    }
  }

  private generateValidVariableName(input: string): string {
    if (!input) return ''

    // Convert to lowercase and replace invalid characters with underscores
    let sanitized = input
      .toLowerCase()
      .replace(/[^a-z0-9_]/g, '_')
      .replace(/_+/g, '_') // Replace multiple underscores with single
      .replace(/^_|_$/g, '') // Remove leading/trailing underscores

    // Ensure it doesn't start with a number
    if (sanitized && /^[0-9]/.test(sanitized)) {
      sanitized = 'var_' + sanitized
    }

    // Ensure it's not empty and has a fallback
    if (!sanitized) {
      sanitized = 'variable'
    }

    return sanitized
  }

  private processDefaultValue(value: any, type: string): any {
    if (!value && value !== 0 && value !== false) return null

    switch (type) {
      case 'boolean':
        // Convert string boolean to actual boolean
        if (value === 'true') return true
        if (value === 'false') return false
        return Boolean(value)
      case 'number':
        // Convert to number if it's a string
        return typeof value === 'string' ? Number(value) || 0 : value
      case 'text':
      case 'url':
      case 'color':
      default:
        return String(value)
    }
  }

  private _fixVariableSlugs(): void {
    this.variablesArray.controls.forEach((variableControl) => {
      const nameValue = variableControl.get('name')?.value
      const slugValue = variableControl.get('slug')?.value

      if (nameValue && slugValue) {
        const sanitizedSlug = this.generateValidVariableName(nameValue)
        if (slugValue !== sanitizedSlug) {
          variableControl.get('slug')?.setValue(sanitizedSlug)
        }
      }
    })
  }
  public headerActionsText(): string {
    if (this.saving) {
      return 'Saving...'
    }
    return this.isEditMode ? 'Update' : 'Create'
  }
}
